import { Menu, Transition } from "@headlessui/react";
import { uniqueId } from "lodash";
import { Fragment, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useFreshDeskScript } from "../hooks/useFreshdeskScript";
import { useAppDispatch, useAppSelector } from "../redux/hook";
import { initFreshChat } from "../services/freshchat.service";
import { classNames } from "../utils/lib";
import {
  Bell,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Gift,
  Headphones,
  LogOut,
  Package,
  Share2,
  ShieldCheck,
  Store,
  Trophy,
  User,
  Users,
  Wallet2,
} from "lucide-react";
import { logoutUser } from "../redux/actions/authActions";

const rowClass = (active: boolean) =>
  classNames(
    active ? "bg-gray-50" : "",
    "group flex items-center justify-between w-full px-4 py-3 text-sm text-gray-900"
  );

const LgAccountBtn = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [isLoadedFreshChat, setLoadedFreshChat] = useState(false);

  function openFreshChat() {
    const fcWidget = (window as any).fcWidget;
    if (fcWidget && fcWidget.isLoaded()) {
      fcWidget.user.setFirstName(user ? user.name.split(" ")[0] : "");
      fcWidget.user.setLastName(user ? user.name.split(" ")[1] : "");
      fcWidget.setExternalId(user ? user.username : uniqueId());
      fcWidget.user.setEmail(user ? user.email : "");
      fcWidget.user.setPhone(user ? user.phone : "");
      fcWidget.show();
      fcWidget.open();
    }
  }

  useFreshDeskScript(() => {
    initFreshChat();
    const fcWidget = (window as any).fcWidget;
    fcWidget.on("widget:loaded", () => fcWidget.hide());
    setLoadedFreshChat(true);
    fcWidget.on("widget:closed", () => fcWidget.hide());
  });

  const openSignOutModal = () => {
    dispatch(logoutUser(true));
  };

  return (
    <Menu as="div" className="relative inline-block text-left font-nunito">
      <Menu.Button className="inline-flex items-center justify-center px-2 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-full">
        <span className="inline-flex h-7 w-7 rounded-full overflow-hidden bg-[#EAF5F4] items-center justify-center">
          <User className="h-4 w-4 text-gray-700" />
        </span>
        <ChevronDown className="ml-1 h-4 w-4 text-gray-500" aria-hidden="true" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="origin-top-right absolute right-0 mt-2 w-80 rounded-2xl shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none overflow-y-auto max-h-[600px]">
          {user && (
            <div className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#EAF5F4] flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-gray-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {user?.name}
                  </div>
                  <div className="text-xs text-gray-500">@{user?.username}</div>
                </div>
                <Link to="/accounts/verify" className="shrink-0">
                  {user?.verified ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#EAF5F4] text-gray-800 text-xs font-medium">
                      <CheckCircle className="w-3 h-3" />
                      Verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-50 text-red-700 text-xs font-medium">
                      <CircleAlert className="w-3 h-3" />
                      Unverified
                    </span>
                  )}
                </Link>
              </div>
            </div>
          )}

          {user && (
            <div className="grid grid-cols-3 gap-2 px-4 pb-3">
              <button
                type="button"
                onClick={() => {
                  if (isLoadedFreshChat) {
                    openFreshChat();
                  }
                }}
                className="bg-[#FAFAFA] rounded-2xl h-20 flex flex-col items-center justify-center gap-1.5"
              >
                <Headphones className="w-5 h-5 text-gray-800" />
                <span className="text-[11px] text-gray-800">Help</span>
              </button>
              <Link
                to="/accounts/referral"
                className="bg-[#FAFAFA] rounded-2xl h-20 flex flex-col items-center justify-center gap-1.5"
              >
                <Gift className="w-5 h-5 text-gray-800" />
                <span className="text-[11px] text-gray-800">Refer & Earn</span>
              </Link>
              <Link
                to="/leaderboard"
                className="bg-[#FAFAFA] rounded-2xl h-20 flex flex-col items-center justify-center gap-1.5"
              >
                <Trophy className="w-5 h-5 text-gray-800" />
                <span className="text-[11px] text-gray-800">Leaderboard</span>
              </Link>
            </div>
          )}

          {user && (
            <div className="py-1 border-t border-gray-100">
              <Menu.Item>
                {({ active }) => (
                  <NavLink to="/accounts" className={rowClass(active)}>
                    <span className="flex items-center">
                      <User className="mr-3 h-5 w-5 text-gray-400" />
                      Account
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </NavLink>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <NavLink to="/accounts/wallet" className={rowClass(active)}>
                    <span className="flex items-center">
                      <Wallet2 className="mr-3 h-5 w-5 text-gray-400" />
                      Wallet
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </NavLink>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <NavLink to="/accounts/myorders" className={rowClass(active)}>
                    <span className="flex items-center">
                      <Package className="mr-3 h-5 w-5 text-gray-400" />
                      Orders
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </NavLink>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <NavLink
                    to="/accounts/notifications"
                    className={rowClass(active)}
                  >
                    <span className="flex items-center">
                      <Bell className="mr-3 h-5 w-5 text-gray-400" />
                      Notifications
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </NavLink>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <NavLink
                    to="/shop/showshopproducts"
                    className={rowClass(active)}
                  >
                    <span className="flex items-center">
                      <Share2 className="mr-3 h-5 w-5 text-gray-400" />
                      Shared products
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </NavLink>
                )}
              </Menu.Item>
              {!user?.verified && (
                <Menu.Item>
                  {({ active }) => (
                    <NavLink to="/accounts/verify" className={rowClass(active)}>
                      <span className="flex items-center">
                        <ShieldCheck className="mr-3 h-5 w-5 text-gray-400" />
                        Verifications
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </NavLink>
                  )}
                </Menu.Item>
              )}
              <Menu.Item>
                {({ active }) => (
                  <a
                    href="https://t.me/+bCkYNwztWf03NDVk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={rowClass(active)}
                  >
                    <span className="flex items-center">
                      <Users className="mr-3 h-5 w-5 text-gray-400" />
                      Community
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </a>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={openSignOutModal}
                    className={classNames(
                      active ? "bg-gray-50" : "",
                      "group flex items-center w-full px-4 py-3 text-sm text-red-500"
                    )}
                  >
                    <LogOut className="mr-3 h-5 w-5" />
                    Sign out
                  </button>
                )}
              </Menu.Item>
            </div>
          )}

          <div className="p-4">
            <a
              href="https://tendo.app/supplier/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between bg-[#EAF5F4] p-4 rounded-2xl"
            >
              <span className="flex items-center text-sm font-medium text-gray-900">
                <Store className="w-5 h-5 mr-2" />
                Become a supplier
              </span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </a>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default LgAccountBtn;
